import { createElement, type HTMLAttributes, type ReactNode } from 'react';
import { cx } from '@/lib/cx';
import styles from './Surface.module.css';

export type SurfaceVariant =
  'glass' | 'glassPanel' | 'glassDark' | 'screen' | 'well' | 'metal' | 'plate';

interface SurfaceProps extends HTMLAttributes<HTMLElement> {
  variant: SurfaceVariant;
  /** Fully rounded ends, for pills and badges. */
  pill?: boolean;
  as?: 'div' | 'section' | 'p' | 'span';
  children?: ReactNode;
}

/**
 * The game's panel materials:
 * - `glass`: frosted ice-blue glass with a cyan rim glow (standalone cards, light pills)
 * - `glassPanel`: the same glass without its own rim, for inside a `Frame`
 * - `screen`: deep navy display, for inside a `Frame` (feedback, readouts)
 * - `well`: a recessed slot sunk into a panel; never looks pressable
 * - `glassDark`: deep navy glass with a bright blue rim (status labels)
 * - `metal`: chamfered steel frame around a blue panel (character and reward cards)
 * - `plate`: bolted light-steel plate (codename and data readouts)
 */
export function Surface({
  variant,
  pill = false,
  as = 'div',
  className,
  children,
  ...rest
}: SurfaceProps) {
  return createElement(
    as,
    { className: cx(styles.surface, styles[variant], pill && styles.pill, className), ...rest },
    children,
  );
}
